"use client";

import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { VoiceInterface } from "@/components/VoiceInterface";
import { getBackendHttpBase } from "@/lib/backendBase";

const DEFAULT_AGENT_ID = "sales_agent";

type UiProfile = {
  name: string;
  role: string;
  character: string;
  objection_level: string;
};

export default function HomePage() {
  const [profile, setProfile] = useState<UiProfile | null>(null);
  const [productName, setProductName] = useState<string | undefined>();
  const [maxSessionMinutes, setMaxSessionMinutes] = useState<number>(30);
  const [error, setError] = useState<string | null>(null);

  const fallbackProfile = useMemo<UiProfile>(
    () => ({
      name: "dr. Ana Novak",
      role: "Pediatrinja s koncesijo",
      character:
        "Strokovna, skeptična do prodajnih predstavnikov. Zahteva dokaze in varnost podatkov.",
      objection_level: "medium",
    }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${getBackendHttpBase()}/config/${DEFAULT_AGENT_ID}`);
        const data = (await resp.json()) as any;
        if (cancelled) return;
        if (data?.error) {
          setError(String(data.error));
          setProfile(fallbackProfile);
          return;
        }
        const p = data?.profile ?? {};
        const prod = data?.product ?? {};
        const b = data?.behavior ?? {};
        if (typeof prod.name === "string" && prod.name.trim()) {
          setProductName(prod.name.trim());
        }
        if (typeof b.max_session_minutes === "number" && Number.isFinite(b.max_session_minutes)) {
          setMaxSessionMinutes(Math.max(0, b.max_session_minutes));
        } else {
          setMaxSessionMinutes(30);
        }
        setProfile({
          name: typeof p.name === "string" ? p.name : fallbackProfile.name,
          role: typeof p.role === "string" ? p.role : fallbackProfile.role,
          character:
            typeof p.character === "string" ? p.character : fallbackProfile.character,
          objection_level:
            typeof p.objection_level === "string"
              ? p.objection_level
              : fallbackProfile.objection_level,
        });
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setProfile(fallbackProfile);
        setMaxSessionMinutes(30);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fallbackProfile]);

  return (
    <AppShell>
      <main>
        {error && (
          <div className="max-w-3xl mx-auto px-6 pt-2 text-xs text-amber-400">
            UI config fetch warning: {error}
          </div>
        )}
        <VoiceInterface
          agentId={DEFAULT_AGENT_ID}
          profile={profile ?? fallbackProfile}
          productName={productName}
          maxSessionMinutes={maxSessionMinutes}
        />
      </main>
    </AppShell>
  );
}
